// Admin → Courses (SPEC §7.7): inline edit, number of teachers and groups, add, archive, delete if unused.

import type { ApiResult } from "../../api/client";
import { createCourse, deleteCourse, fetchCourses, setCourseArchived, updateCourse } from "../../api/courses";
import type { Course } from "../../api/types";
import { currentLocale, t } from "../../i18n/i18n";
import { setRedraw } from "../../router";
import { isAdmin } from "../../session";
import { createNameComparer } from "../../text";
import { createConfirmButton } from "../../ui/confirmButton";
import { createButton, createElement } from "../../ui/dom";
import { createInputField, type Field } from "../../ui/forms";
import { reportAndReload } from "../../ui/resultMessage";
import { createLoading, showPageProblem, startPage } from "../pageFrame";

export async function renderCoursesPage(container: HTMLElement): Promise<void> {
  const page: HTMLElement = startPage(container, t("admin.courses"));
  page.append(createLoading());
  const courses: ApiResult<Course[]> = await fetchCourses(true);
  if (courses.ok === false) {
    showPageProblem(page, courses.problem);
    return;
  }

  const draw = (): void => {
    drawPage(startPage(container, t("admin.courses")), courses.data);
  };
  setRedraw(container, draw);
  draw();
}

function drawPage(page: HTMLElement, courses: Course[]): void {
  const compare: (first: string, second: string) => number = createNameComparer(currentLocale());
  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("app.name"), t("app.note"), t("admin.teacherCount"), t("admin.groupCount"), ""]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const course of [...courses].sort((first, second): number => compare(first.name, second.name))) {
    body.append(buildRow(course));
  }

  const wrapper: HTMLDivElement = createElement("div", "table-scroll");
  wrapper.append(table);
  page.append(wrapper);
  if (isAdmin()) {
    page.append(buildAddForm());
  }
}

function buildRow(course: Course): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  const name: HTMLInputElement = createInput(course.name);
  const note: HTMLInputElement = createInput(course.note ?? "");
  if (isAdmin()) {
    appendInputCells(row, [name, note]);
  } else {
    row.append(createElement("td", "", course.name), createElement("td", "", course.note ?? ""));
  }

  row.append(createElement("td", "", String(course.teacherCount)), createElement("td", "",
    String(course.groupCount)));
  const actions: HTMLTableCellElement = row.insertCell();
  if (course.archived) {
    actions.append(createElement("span", "badge badge-neutral", t("app.archived")));
  }

  if (isAdmin()) {
    actions.append(...buildActions(course, name, note));
  }

  return row;
}

function buildActions(course: Course, name: HTMLInputElement, note: HTMLInputElement): HTMLElement[] {
  return [
    createButton(t("app.save"), async (): Promise<void> => {
      await reportAndReload(await updateCourse(course.id, name.value, note.value, course.version));
    }),
    createButton(course.archived ? t("app.unarchive") : t("app.archive"), async (): Promise<void> => {
      await reportAndReload(await setCourseArchived(course.id, course.archived === false, course.version));
    }),
    createConfirmButton(t("app.delete"), async (): Promise<void> => {
      await reportAndReload(await deleteCourse(course.id));
    }),
  ];
}

function createInput(value: string): HTMLInputElement {
  const input: HTMLInputElement = createElement("input");
  input.value = value;
  return input;
}

function appendInputCells(row: HTMLTableRowElement, inputs: HTMLInputElement[]): void {
  for (const input of inputs) {
    const cell: HTMLTableCellElement = row.insertCell();
    cell.append(input);
  }
}

function buildAddForm(): HTMLElement {
  const name: Field<HTMLInputElement> = createInputField(t("app.name"), "text", "");
  const form: HTMLDivElement = createElement("div", "toolbar");
  form.append(name.wrapper, createButton(t("admin.addCourse"), async (): Promise<void> => {
    await reportAndReload(await createCourse(name.input.value, ""));
  }, "button-primary"));
  return form;
}
